import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { DayButtonProps, DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import { CustomPriceModel } from '../../../../../shared/ui-models/unit-type.ui-model';

type Field = 'checkIn' | 'checkOut';

export interface BlockedRange {
  from: number;
  to: number;
}

export interface StayDatesPickerProps {
  data: {
    checkIn?: Date;
    checkOut?: Date;
    checkInBlocked: BlockedRange[];
    checkOutBlocked: BlockedRange[];
    basePrice?: number;
    customPrices?: CustomPriceModel[];
    isLoading?: boolean;
  };
  eventHandlers: {
    onChange: (dates: Date[]) => void;
  };
}

const DAY_KEY = 'YYYY-MM-DD';

const toDayKeys = (ranges: BlockedRange[]) => {
  const keys = new Set<string>();
  ranges.forEach(({ from, to }) => {
    const end = moment(to).startOf('day');
    for (const day = moment(from).startOf('day'); day.isSameOrBefore(end); day.add(1, 'day')) {
      keys.add(day.format(DAY_KEY));
    }
  });
  return keys;
};

const formatPrice = (price: number) =>
  price >= 10000 ? `${Math.round(price / 1000)}k` : Math.round(price).toLocaleString('en-US');

const pickerStyle = {
  '--rdp-accent-color': '#237c58',
  '--rdp-accent-background-color': '#e9f2ee',
  '--rdp-day-width': '48px',
  '--rdp-day-height': '48px',
  '--rdp-day_button-width': '46px',
  '--rdp-day_button-height': '46px',
  '--rdp-day_button-border-radius': '12px',
  '--rdp-selected-border': '0',
  '--rdp-today-color': '#237c58'
} as React.CSSProperties;

export const StayDatesPicker: React.FC<StayDatesPickerProps> = ({
  data: { checkIn, checkOut, checkInBlocked, checkOutBlocked, basePrice, customPrices, isLoading },
  eventHandlers: { onChange }
}) => {
  const [openField, setOpenField] = useState<Field | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const today = moment().startOf('day');

  const checkInBlockedKeys = useMemo(() => toDayKeys(checkInBlocked), [checkInBlocked]);
  const checkOutBlockedKeys = useMemo(() => toDayKeys(checkOutBlocked), [checkOutBlocked]);

  useEffect(() => {
    if (!openField) return;
    const close = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpenField(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openField]);

  const firstBlockedNightAfter = (start: Date) => {
    const limit = moment(start).add(2, 'years');
    for (const day = moment(start).add(1, 'day'); day.isBefore(limit); day.add(1, 'day')) {
      if (checkInBlockedKeys.has(day.format(DAY_KEY))) return day.clone();
    }
    return null;
  };

  const isValidCheckOut = (start: Date, end: Date) => {
    const endDay = moment(end).startOf('day');
    if (!endDay.isAfter(moment(start).startOf('day'))) return false;
    if (checkOutBlockedKeys.has(endDay.format(DAY_KEY))) return false;
    const blockedNight = firstBlockedNightAfter(start);
    return !blockedNight || endDay.isSameOrBefore(blockedNight);
  };

  const isCheckInDisabled = (date: Date) =>
    moment(date).isBefore(today) || checkInBlockedKeys.has(moment(date).format(DAY_KEY));

  const isCheckOutDisabled = (date: Date) => !checkIn || !isValidCheckOut(checkIn, date);

  const selectCheckIn = (date?: Date) => {
    if (!date) return;
    if (checkOut && isValidCheckOut(date, checkOut)) {
      onChange([date, checkOut]);
      setOpenField(null);
      return;
    }
    onChange([date]);
    setOpenField('checkOut');
  };

  const selectCheckOut = (date?: Date) => {
    if (!date || !checkIn) return;
    onChange([checkIn, date]);
    setOpenField(null);
  };

  const priceFor = (date: Date) => {
    const custom = customPrices?.find((item) =>
      moment(date).isBetween(
        moment(item.from).startOf('day'),
        moment(item.to).endOf('day'),
        undefined,
        '[]'
      )
    );
    return custom ? Number(custom.price) : basePrice;
  };

  const PricedDayButton = ({ day, modifiers, ...buttonProps }: DayButtonProps) => {
    const price = basePrice !== undefined ? priceFor(day.date) : undefined;
    return (
      <button {...buttonProps}>
        <span className="flex flex-col items-center leading-none gap-[3px]">
          <span className="text-[14px]">{day.date.getDate()}</span>
          {!!price && !modifiers.disabled && (
            <span
              className={`text-[9px] ${modifiers.selected ? 'text-neutral-0' : 'text-primary-500'}`}
            >
              {formatPrice(price)}
            </span>
          )}
        </span>
      </button>
    );
  };

  const nights = checkIn && checkOut ? moment(checkOut).diff(moment(checkIn), 'days') : 0;
  const stayDays =
    checkIn && checkOut
      ? Array.from({ length: Math.max(nights - 1, 0) }, (_, index) =>
          moment(checkIn)
            .add(index + 1, 'days')
            .toDate()
        )
      : [];

  const triggerClass = (field: Field) =>
    `flex-1 min-w-0 flex flex-col items-start gap-[4px] px-[16px] py-[10px] bg-neutral-0 text-left cursor-pointer transition-colors hover:bg-creams-100 disabled:cursor-not-allowed disabled:hover:bg-neutral-0 ${
      openField === field ? 'bg-creams-100' : ''
    }`;

  return (
    <div ref={containerRef} className="relative">
      <div
        className={`flex items-stretch rounded-[12px] border divide-x divide-gray-200 overflow-hidden ${
          openField ? 'border-neutral-500' : 'border-gray-200'
        }`}
      >
        <button
          type="button"
          className={triggerClass('checkIn')}
          disabled={isLoading}
          onClick={() => setOpenField(openField === 'checkIn' ? null : 'checkIn')}
        >
          <span className="text-[12px] font-gilroy-medium text-neutral-300">Check-in</span>
          <span className={`text-[15px] ${checkIn ? 'text-neutral-500' : 'text-neutral-100'}`}>
            {isLoading
              ? 'Loading availability…'
              : checkIn
              ? moment(checkIn).format('ddd, D MMM YYYY')
              : 'Select date'}
          </span>
        </button>
        <button
          type="button"
          className={triggerClass('checkOut')}
          disabled={isLoading || !checkIn}
          onClick={() => setOpenField(openField === 'checkOut' ? null : 'checkOut')}
        >
          <span className="text-[12px] font-gilroy-medium text-neutral-300">Check-out</span>
          <span className={`text-[15px] ${checkOut ? 'text-neutral-500' : 'text-neutral-100'}`}>
            {checkOut
              ? `${moment(checkOut).format('ddd, D MMM YYYY')} · ${nights} ${
                  nights === 1 ? 'night' : 'nights'
                }`
              : 'Select date'}
          </span>
        </button>
      </div>

      {openField && (
        <div className="absolute left-0 top-[calc(100%+8px)] z-50 rounded-[16px] border border-gray-200 bg-neutral-0 p-[16px] shadow-elevation-2">
          <DayPicker
            key={openField}
            mode="single"
            numberOfMonths={2}
            weekStartsOn={6}
            showOutsideDays={false}
            style={pickerStyle}
            className="font-gilroy-regular text-neutral-500"
            classNames={{
              months: 'flex gap-[24px] relative',
              month_caption: 'h-[40px] flex items-center text-[15px] font-gilroy-medium',
              weekday: 'text-[12px] font-gilroy-medium text-neutral-300 pb-[6px]',
              chevron: 'fill-neutral-500'
            }}
            modifiers={{ stay: stayDays, stayStart: checkIn ? [checkIn] : [] }}
            modifiersClassNames={{
              stay: '!bg-primary-50',
              stayStart: '!text-primary-700 font-gilroy-medium',
              disabled: '!text-neutral-100 [&_button]:!cursor-not-allowed'
            }}
            components={{ DayButton: PricedDayButton }}
            startMonth={today.toDate()}
            defaultMonth={
              openField === 'checkOut'
                ? checkOut || checkIn || today.toDate()
                : checkIn || today.toDate()
            }
            selected={openField === 'checkIn' ? checkIn : checkOut}
            onSelect={openField === 'checkIn' ? selectCheckIn : selectCheckOut}
            disabled={openField === 'checkIn' ? isCheckInDisabled : isCheckOutDisabled}
          />
        </div>
      )}
    </div>
  );
};
